import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Button, Stack, IconButton, Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem
} from '@mui/material';
import { Plus, SquarePen } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { getCmsContents, createCmsContent, updateCmsContent, getCmsByType } from '../../Services/ContentHub/CmsToolsApi';
import { stripHtml } from "string-strip-html";
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';

const quillModules = {
    toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['link'],
        ['clean']
    ]
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const GetCmsTools = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [cmsContents, setCmsContents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedFilter, setSelectedFilter] = useState('all');

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentContent, setCurrentContent] = useState(null);
    const [formData, setFormData] = useState({
        type: '',
        content: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    // Types for CMS content
    const contentTypes = [
        { value: 'privacy_policy', label: 'Privacy Policy' },
        { value: 'terms_and_conditions', label: 'Terms & Conditions' },
        { value: 'cancelations_policy', label: 'Cancellations Policy' }
    ];

    // Fetch CMS contents
    const fetchCmsContents = useCallback(async (signal) => {
        try {
            setLoading(true);
            let response;
            if (selectedFilter === 'all') {
                response = await getCmsContents({ signal });
            } else {
                response = await getCmsByType(selectedFilter);
                // Wrap in array if it returns a single item instead of array
                if (response.success && !Array.isArray(response.data)) {
                    response.data = response.data ? [response.data] : [];
                }
            }

            if (response && response.success) {
                setCmsContents(response.data);
            } else {
                showSnackbar(response?.message || "Failed to load CMS contents.", "error");
                setCmsContents([]);
            }
        } catch (err) {
            if (!signal?.aborted) {
                showSnackbar("Failed to fetch CMS contents. Please try again later.", "error");
                setCmsContents([]);
            }
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, [selectedFilter, showSnackbar]);

    useEffect(() => {
        const controller = new AbortController();
        fetchCmsContents(controller.signal);
        return () => controller.abort();
    }, [fetchCmsContents]);

    const handleOpenModal = (content = null) => {
        setFieldErrors({});
        if (content) {
            setIsEdit(true);
            setCurrentContent(content);
            setFormData({
                type: content.type || '',
                content: content.content || ''
            });
        } else {
            setIsEdit(false);
            setCurrentContent(null);
            setFormData({
                type: '',
                content: ''
            });
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setFieldErrors({});
        setOpenModal(false);
        setIsEdit(false);
        setCurrentContent(null);
        setFormData({
            type: '',
            content: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleQuillChange = (value) => {
        setFormData(prev => ({
            ...prev,
            content: value
        }));
        if (fieldErrors.content) {
            setFieldErrors(prev => ({
                ...prev,
                content: null
            }));
        }
    };

    const handleSubmit = async () => {
        const errs = {};
        if (!formData.type) errs.type = "Content Type is required";
        if (!formData.content) errs.content = "Content is required";

        if (Object.keys(errs).length > 0) {
            setFieldErrors(errs);
            showSnackbar("Please fill in all required fields", "error");
            return;
        }

        setSubmitting(true);
        setFieldErrors({});

        try {
            if (isEdit) {
                await updateCmsContent(currentContent.id, formData);
                showSnackbar("CMS content updated successfully!", "success");
            } else {
                await createCmsContent(formData);
                showSnackbar("CMS content created successfully!", "success");
            }

            handleCloseModal();
            // Refresh the list
            const controller = new AbortController();
            fetchCmsContents(controller.signal);
        } catch (err) {
            console.error("Error saving CMS content:", err);
            const serverError = err.response?.data;
            const errMsg = serverError?.errors?.[0] ||
                serverError?.details?.[0]?.message ||
                serverError?.message ||
                err.message ||
                `Failed to ${isEdit ? 'update' : 'create'} CMS content`;

            showSnackbar(errMsg, "error");

            if (serverError && serverError.details && Array.isArray(serverError.details)) {
                const errors = {};
                serverError.details.forEach(detail => {
                    errors[detail.field] = detail.message;
                });
                setFieldErrors(errors);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        CMS Tools
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <TextField
                            select
                            size="small"
                            value={selectedFilter}
                            onChange={(e) => setSelectedFilter(e.target.value)}
                            sx={{
                                minWidth: 180,
                                bgcolor: '#fff',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    height: '40px',
                                    fontSize: '0.85rem',
                                    '& fieldset': { borderColor: '#E0E0E0' },
                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                }
                            }}
                        >
                            <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>All Types</MenuItem>
                            {contentTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.85rem' }}>{type.label}</MenuItem>
                            ))}
                        </TextField>
                        <PermissionWrapper permission="create" mode="disable">
                            <Button
                                disableElevation
                                disableRipple
                                disableFocusRipple
                                disableTouchRipple
                                variant="contained"
                                startIcon={<Plus size={18} />}
                                onClick={() => handleOpenModal()}
                                sx={{
                                    bgcolor: '#FFBD65',

                                    px: 2.5,
                                    color: '#fff',
                                    borderRadius: '6px',
                                    height: '40px',
                                    textTransform: 'none',
                                    fontSize: '0.85rem'
                                }}
                            >
                                Add CMS Content
                            </Button>
                        </PermissionWrapper>
                    </Stack>
                </Box>

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 950 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '180px' }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Content Snippet</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px', textAlign: 'right' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : cmsContents.length > 0 ? (
                                    cmsContents.map((item, idx) => (
                                        <TableRow key={item.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#333', fontWeight: 500 }}>
                                                {contentTypes.find(t => t.value === item.type)?.label || item.type}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>
                                                {item.content
                                                    ? stripHtml(item.content).result.substring(0, 100) + "..."
                                                    : ""}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small" sx={{ color: '#FFBD65' }} onClick={() => handleOpenModal(item)}>
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No CMS content found. Click "Add CMS Content" to create one.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Add/Edit Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        {isEdit ? 'Edit CMS Content' : 'Add CMS Content'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>Content Type *</Typography>
                                <TextField
                                    select
                                    fullWidth
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    size='small'
                                    error={Boolean(fieldErrors.type)}
                                    helperText={fieldErrors.type}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                >
                                    {contentTypes.map((option) => (
                                        <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.85rem' }}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>Content *</Typography>
                                <Box sx={{
                                    border: fieldErrors.content ? '1px solid #FF4D49' : 'none',
                                    borderRadius: '6px',
                                    '& .ql-toolbar': { borderTopLeftRadius: '6px', borderTopRightRadius: '6px', borderColor: '#E0E0E0' },
                                    '& .ql-container': { borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px', borderColor: '#E0E0E0', fontSize: '0.85rem', minHeight: '200px' }
                                }}>
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.content}
                                        onChange={handleQuillChange}
                                        placeholder="Enter content here..."
                                        style={{
                                            height: '250px',
                                            marginBottom: '45px'
                                        }}
                                        modules={quillModules}
                                    />
                                </Box>
                                {fieldErrors.content && (
                                    <Typography variant="caption" sx={{ color: '#FF4D49', mt: 0.5, display: 'block', fontSize: '0.75rem' }}>
                                        {fieldErrors.content}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleCloseModal}
                            sx={{
                                borderRadius: '6px',
                                textTransform: 'none',
                                color: '#222',
                                px: 2.5,
                                fontSize: '0.85rem'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleSubmit}
                            disabled={submitting}
                            variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',

                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 3,
                                color: '#fff',
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Add')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </PermissionWrapper>
    );
};

export default GetCmsTools;